import { ConfirmModal } from "@components/ConfirmModal";
import { Modal } from "@components/Modal";
import { DateInput } from "@components/DateInput";
import { BackIcon } from "@components/Icons/Back";
import { LanguageIcon } from "@components/Icons/Language";
import { FullPicker } from "@components/ImagePicker/FullPicker";
import { Input } from "@components/Input";
import { Textarea } from "@components/Input/Textarea";
import { Loading } from "@components/Loading";
import { Toggle } from "@components/Toggle";
import { convertToPng } from "@helpers/compress";
import { useLocalePreference } from "@helpers/useLocalePreference";
import { usePersistedState } from "@helpers/usePersistedState";
import { useTranslatedKey } from "@helpers/useTranslatedKey";
import { useTranslation } from "@helpers/useTranslation";
import { getLocaleFlag, useLocales } from "src/helpers/locales";
import { Timestamp, doc, setDoc } from "firebase/firestore";
import { ref as storageRef, uploadBytes } from "firebase/storage";
import { useContext, useEffect, useReducer, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { FirebaseContext } from "src/helpers/firebase";
import { useBranding } from "src/helpers/useBranding";
import { useEvent, useEventId } from "src/helpers/useEvent";
import { useEventNavigate } from "src/helpers/useEventNavigate";
import { useEventTypes } from "src/helpers/useEventTypes";
import { useToast } from "src/helpers/useToast";
import { BettingConfigModal } from "./BettingConfigModal";
import { HowBettingWorksModal } from "@components/HowBettingWorksModal";

export const Customise = () => {
  const dateInputRef = useRef<HTMLInputElement | null>(null);
  const handleShowPicker = () => dateInputRef?.current?.showPicker && dateInputRef.current.showPicker();
  const [saving, setSaving] = useState(false);

  const eventTypes = useEventTypes();

  const { firestore, storage } = useContext(FirebaseContext);
  const { t, i18n } = useTranslation("admin");
  const { setLocale } = useLocalePreference();
  const { getKeyName, getTranslatedValue } = useTranslatedKey();
  const [event, loadingEvent, errorEvent] = useEvent();
  const eventNavigate = useEventNavigate();

  useEffect(() => {
    if (errorEvent) console.error(errorEvent);
  }, [errorEvent]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isDirty },
  } = useForm<{
    name: string;
    pin: string | number;
    about: string;
    type: string;
    locales: {
      en: boolean;
      lt: boolean;
      sv: boolean;
      de: boolean;
      es: boolean;
      it: boolean;
      fr: boolean;
      pl: boolean;
      lv: boolean;
      pt: boolean;
      zh: boolean;
    };
    pointDegrade: boolean;
    showGuestGalleryTab: boolean;
    skipCreateTeam: boolean;
    hideHighscores: boolean;
    playersOnly: boolean;
    uploadersOnly: boolean;
    bettingEnabled: boolean;
    bettingPaymentUrl: string;
    shareWinningsWithHost: boolean;
    oracleMode: boolean;
    landingConfig: {
      enabled: boolean;
      showCountdown: boolean;
      invitationCopy: string;
      showDate: boolean;
      showLocation: boolean;
      locationName: string;
      locationCoords: string;
    };
    subTitle: string;
  }>({
    defaultValues: {
      name: event?.name ?? "",
      pin: event?.pin ?? "",
      about: getTranslatedValue(event, "about") ?? "",
      subTitle: event?.subTitle ?? "",
      type: event?.type ?? eventTypes[0].value,
      locales: {
        en: event?.locales.en ?? false,
        lt: event?.locales.lt ?? false,
      },
      pointDegrade: event?.mainConfig?.pointDegrade ?? event?.pointDegrade ?? false,
      showGuestGalleryTab: event?.mainConfig?.showGuestGalleryTab ?? event?.showGuestGalleryTab ?? false,
      skipCreateTeam: event?.mainConfig?.skipCreateTeam ?? false,
      hideHighscores: event?.mainConfig?.hideHighscores ?? false,
      playersOnly: event?.mainConfig?.playersOnly ?? false,
      uploadersOnly: event?.mainConfig?.uploadersOnly ?? false,
      bettingEnabled: event?.mainConfig?.bettingEnabled ?? false,
      bettingPaymentUrl: event?.mainConfig?.bettingPaymentUrl ?? "",
      shareWinningsWithHost: event?.mainConfig?.shareWinningsWithHost ?? false,
      oracleMode: event?.mainConfig?.oracleMode ?? false,
      landingConfig: {
        enabled: event?.landingConfig?.enabled ?? false,
        showCountdown: event?.landingConfig?.showCountdown ?? false,
        invitationCopy: getTranslatedValue(event?.landingConfig, "invitationCopy") ?? "",
        showDate: event?.landingConfig?.showDate ?? true,
        showLocation: event?.landingConfig?.showLocation ?? false,
        locationName: event?.landingConfig?.locationName ?? "",
        locationCoords: event?.landingConfig?.locationCoords ?? "",
      },
    },
  });

  useEffect(() => {
    reset(event);
    setValue("pointDegrade", event?.mainConfig?.pointDegrade ?? event?.pointDegrade ?? false);
    setValue("showGuestGalleryTab", event?.mainConfig?.showGuestGalleryTab ?? event?.showGuestGalleryTab ?? false);
    setValue("skipCreateTeam", event?.mainConfig?.skipCreateTeam ?? false);
    setValue("hideHighscores", event?.mainConfig?.hideHighscores ?? false);
    setValue("playersOnly", event?.mainConfig?.playersOnly ?? false);
    setValue("uploadersOnly", event?.mainConfig?.uploadersOnly ?? false);
    setValue("bettingEnabled", event?.mainConfig?.bettingEnabled ?? false);
    setValue("bettingPaymentUrl", event?.mainConfig?.bettingPaymentUrl ?? "");
    setValue("shareWinningsWithHost", event?.mainConfig?.shareWinningsWithHost ?? false);
    setValue("oracleMode", event?.mainConfig?.oracleMode ?? false);
    // reset(event) above seeds about/invitationCopy from the base fields; override with the
    // active locale's slot (falling back to base when empty) — see useTranslatedKey.
    setValue("about", getTranslatedValue(event, "about") ?? "");
    setValue("landingConfig.invitationCopy", getTranslatedValue(event?.landingConfig, "invitationCopy") ?? "");
  }, [event]);

  // Track which locale the admin is currently editing so we can detect mid-edit language switches.
  const [editingLocale, setEditingLocale] = useState<AppLocale>(i18n.language as AppLocale);

  const topNavLocales = useLocales();

  const eventId = useEventId();

  const [image, setImage] = useState(undefined);
  const [logo, setLogo] = useState(undefined);
  const [defaultAvatar, setDefaultAvatar] = useState(undefined);
  const [activeTab, setActiveTab] = useState<number>(1);

  //https://reactjs.org/docs/hooks-faq.html#is-there-something-like-forceupdate
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);

  const toast = useToast();

  const { appDomain } = useBranding();

  const [showDegradeConfirm, setShowDegradeConfirm] = useState(false);
  const [showSkipCreateTeamConfirm, setShowSkipCreateTeamConfirm] = useState(false);
  const [showPlayersOnlyConfirm, setShowPlayersOnlyConfirm] = useState(false);
  const [showUploadersOnlyConfirm, setShowUploadersOnlyConfirm] = useState(false);
  const [showBettingEnabledConfirm, setShowBettingEnabledConfirm] = useState(false);
  const [showOracleModeConfirm, setShowOracleModeConfirm] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const [showBettingModal, setShowBettingModal] = useState(false);

  // Check if there are unsaved changes (form is dirty or images have been selected)
  const hasUnsavedChanges = isDirty || image || logo || defaultAvatar;

  // If the admin switches UI language mid-edit (via TopNav), either re-seed the locale-bound
  // fields silently or, when there are unsaved changes, reuse the unsaved-changes modal.
  useEffect(() => {
    const next = i18n.language as AppLocale;
    if (next === editingLocale) return;

    if (!hasUnsavedChanges) {
      setValue("about", getTranslatedValue(event, "about") ?? "");
      setValue("landingConfig.invitationCopy", getTranslatedValue(event?.landingConfig, "invitationCopy") ?? "");
      setEditingLocale(next);
      return;
    }

    // Snap the language back to what's in the form, then offer the standard prompt. The pending
    // action re-applies the switch — for save-and-leave the form is no longer dirty by then; for
    // leave-without-saving we discard form state first so the subsequent re-seed picks up cleanly.
    setLocale(editingLocale);
    setPendingNavigation(() => () => {
      reset(event);
      setImage(undefined);
      setLogo(undefined);
      setLocale(next);
    });
    setShowUnsavedChangesModal(true);
  }, [i18n.language]);

  // Handle navigation with unsaved changes check
  const handleNavigation = (navigationFn: () => void) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(() => navigationFn);
      setShowUnsavedChangesModal(true);
    } else {
      navigationFn();
    }
  };

  // Handle save and leave
  const handleSaveAndLeave = async () => {
    try {
      await handleSubmit(onSubmit)();
      setShowUnsavedChangesModal(false);
      if (pendingNavigation) {
        pendingNavigation();
        setPendingNavigation(null);
      }
    } catch (error) {
      // If save fails, don't navigate
      console.error("Failed to save:", error);
    }
  };

  // Handle leave without saving
  const handleLeaveWithoutSaving = () => {
    setShowUnsavedChangesModal(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  };

  const onSubmit = async ({
    locales,
    name,
    pin,
    type,
    about,
    subTitle,
    date,
    pointDegrade,
    showGuestGalleryTab,
    skipCreateTeam,
    hideHighscores,
    playersOnly,
    uploadersOnly,
    bettingEnabled,
    bettingPaymentUrl,
    shareWinningsWithHost,
    oracleMode,
    landingConfig,
  }) => {
    setSaving(true);
    if (image) {
      const imagePath = `events/${eventId}/background`;
      const imageRef = storageRef(storage, imagePath);
      await uploadBytes(imageRef, image);
    }

    if (logo) {
      const logoPath = `events/${eventId}/logo.png`;
      const logoRef = storageRef(storage, logoPath);
      // Served raw (no backend processing), so downscale + re-encode as PNG to keep transparency
      await uploadBytes(logoRef, await convertToPng(logo));
    }

    if (defaultAvatar) {
      const defaultAvatarPath = `events/${eventId}/defaultAvatar_heavy`;
      const defaultAvatarRef = storageRef(storage, defaultAvatarPath);
      await uploadBytes(defaultAvatarRef, defaultAvatar);
    }

    try {
      const docRef = doc(firestore, `events/${eventId}`);
      const invitationCopyValue = landingConfig?.invitationCopy ?? "";
      await setDoc(
        docRef,
        {
          locales,
          name,
          pin,
          type,
          // Write both the base field (last-write fallback for unlocalised locales) and the
          // current admin locale's slot — same pattern as challenge titles/copy/about.
          about,
          [getKeyName("about")]: about,
          subTitle: subTitle ?? "",
          date,
          // pointDegrade: pointDegrade ?? false, // deprecated
          // showGuestGalleryTab: showGuestGalleryTab ?? false, // deprecated
          mainConfig: {
            pointDegrade: pointDegrade ?? false,
            showGuestGalleryTab: showGuestGalleryTab ?? false,
            skipCreateTeam: skipCreateTeam ?? false,
            hideHighscores: hideHighscores ?? false,
            playersOnly: playersOnly ?? false,
            uploadersOnly: uploadersOnly ?? false,
            bettingEnabled: bettingEnabled ?? false,
            bettingPaymentUrl: bettingPaymentUrl ?? "",
            shareWinningsWithHost: shareWinningsWithHost ?? false,
            oracleMode: oracleMode ?? false,
          },
          landingConfig: {
            enabled: landingConfig?.enabled ?? false,
            showCountdown: landingConfig?.showCountdown ?? false,
            invitationCopy: invitationCopyValue,
            [getKeyName("invitationCopy")]: invitationCopyValue,
            showDate: landingConfig?.showDate ?? true,
            showLocation: landingConfig?.showLocation ?? false,
            locationName: landingConfig?.locationName ?? "",
            locationCoords: landingConfig?.locationCoords ?? "",
          },
          lastModifiedOn: Timestamp.now(),
        },
        { merge: true },
      );
    } catch (error) {
      console.error(error);
    }

    setSaving(false);
    toast(t("updated"), "👍", "success", 1500);

    // Reset form state to clear isDirty flag and image selections
    setImage(undefined);
    setLogo(undefined);
    setDefaultAvatar(undefined);
    reset(getValues()); // This will mark the form as not dirty
  };

  const values = getValues();
  const ltLocale = values.locales?.lt || false;
  const enLocale = values.locales?.en || false;
  const svLocale = values.locales?.sv || false;
  const deLocale = values.locales?.de || false;
  const esLocale = values.locales?.es || false;
  const itLocale = values.locales?.it || false;
  const frLocale = values.locales?.fr || false;
  const plLocale = values.locales?.pl || false;
  const lvLocale = values.locales?.lv || false;
  const ptLocale = values.locales?.pt || false;
  const zhLocale = values.locales?.zh || false;

  // Helper function to handle language toggle logic
  const handleLanguageToggle = (
    langKey: "en" | "lt" | "sv" | "de" | "es" | "it" | "fr" | "pl" | "lv" | "pt" | "zh",
    currentValue: boolean,
  ) => {
    const allLocales = {
      lt: ltLocale,
      en: enLocale,
      sv: svLocale,
      de: deLocale,
      es: esLocale,
      it: itLocale,
      fr: frLocale,
      pl: plLocale,
      lv: lvLocale,
      pt: ptLocale,
      zh: zhLocale,
    };
    const enabledLocales = Object.entries(allLocales).filter(([_, enabled]) => enabled);
    const currentLanguage = i18n.language;

    // If we're trying to disable the last enabled language, enable English as fallback
    if (currentValue && enabledLocales.length === 1) {
      setValue("locales.en", true, { shouldDirty: true });
      if (langKey !== "en") {
        setValue(`locales.${langKey}` as any, false, { shouldDirty: true });
        // If we're disabling the current language, switch to English
        if (langKey === currentLanguage) {
          setLocale("en");
        }
      }
    } else {
      setValue(`locales.${langKey}` as any, !currentValue, { shouldDirty: true });

      // If we're disabling the currently active language, switch to the first available enabled language
      if (currentValue && langKey === currentLanguage) {
        const remainingLocales = Object.entries(allLocales).filter(([key, enabled]) => enabled && key !== langKey);
        if (remainingLocales.length > 0) {
          const newLanguage = remainingLocales[0][0] as AppLocale;
          setLocale(newLanguage);
        }
      }
    }

    forceUpdate();
  };

  return (
    <Loading loading={!event || loadingEvent}>
      <div className="flex flex-col">
        <div className="navbar absolute sticky top-0 z-10 border-b-2 px-2 shadow-lg shadow-base-300">
          <div className="flex w-full items-center justify-between px-2">
            <a onClick={() => handleNavigation(() => eventNavigate("/admin"))} className="link cursor-pointer">
              <div className="flex flex-row font-light">
                <BackIcon className="mr-1 h-6 w-6" />
                <span>{t("back")}</span>
              </div>
            </a>
            {/* Mirrors the TopNav admin language picker — clicking a flag triggers setLocale, which
                our [i18n.language] effect picks up (silent re-seed when clean, unsaved-changes
                modal when dirty). */}
            <div className="dropdown-end dropdown">
              <label tabIndex={0} className="btn-primary btn-ghost btn-circle btn">
                <LanguageIcon className="hidden" />
                <div className="text-lg">{getLocaleFlag(i18n.language as AppLocale)}</div>
              </label>
              <ul
                tabIndex={0}
                className="dropdown-content menu rounded-box menu-compact mt-3 border border-base-300 bg-base-200 p-2 shadow"
              >
                {topNavLocales.length > 1 && (
                  <li className="flex justify-center align-bottom">
                    {topNavLocales.map((locale) => (
                      <button
                        key={locale}
                        className={`my-1 flex items-center rounded-lg border-2 border-base-300 bg-base-200 ${
                          locale === i18n.language ? "border-primary font-semibold underline" : ""
                        }}`}
                        onClick={() => {
                          setLocale(locale);
                          (document?.activeElement as any)?.blur();
                        }}
                      >
                        <span className="text-xs uppercase text-neutral">{locale}</span>
                        <span className="text-lg">{getLocaleFlag(locale)}</span>
                      </button>
                    ))}
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
        <main className="flex-1 flex-col">
          <div className="flex flex-col px-6 py-4">
            <h1 className="mt-12 mb-4 text-center text-3xl font-semibold">{t("customise")}</h1>
            <h4 className="pt-4 text-center text-sm font-semibold text-neutral">{`${appDomain}/${eventId}`}</h4>
            <div className="flex justify-center">
              <p className="mt-12 max-w-lg text-center font-light">{t("customiseAbout")}</p>
            </div>
            <div className="mt-12 flex w-full flex-col items-center px-4">
              <form onSubmit={handleSubmit(onSubmit)} className="h-100 flex w-full max-w-lg flex-col items-center py-4">
                <div className="tabs mb-8 mt-8 w-full">
                  <a
                    className={`tab-bordered tab ${activeTab === 1 ? "tab-active" : ""} w-1/3`}
                    onClick={() => setActiveTab(1)}
                  >
                    {t("details")}
                  </a>
                  <a
                    className={`tab-bordered tab ${activeTab === 2 ? "tab-active" : ""} w-1/3`}
                    onClick={() => setActiveTab(2)}
                  >
                    {t("landingPage")}
                  </a>
                  <a
                    className={`tab-bordered tab ${activeTab === 3 ? "tab-active" : ""} w-1/3`}
                    onClick={() => setActiveTab(3)}
                  >
                    {t("logo")}
                  </a>
                </div>
                <div className={`w-full ${!activeTab || activeTab === 1 ? "" : "hidden"}`}>
                  <div className="align-start mb-2 flex flex-row" onClick={() => handleShowPicker()}>
                    <DateInput
                      ref={dateInputRef}
                      label={t("dateField")}
                      name="date"
                      register={register}
                      errors={errors}
                    />
                  </div>
                  <div className="mb-4">
                    <Input
                      label={t("eventName")}
                      name="name"
                      placeholder={t("eventNameExample")}
                      required
                      requiredMessage={t("fieldRequired")}
                      errors={errors}
                      register={register}
                    />
                  </div>
                  <div className="mb-4">
                    <Input
                      label={t("pinCode")}
                      name="pin"
                      placeholder={t("pinCodeExample")}
                      pattern="^\d{4}$"
                      title={t("pinCodeValidationError")}
                      required
                      requiredMessage={t("fieldRequired")}
                      errors={errors}
                      register={register}
                    />
                  </div>
                  <div className="mb-4">
                    <Textarea
                      placeholder={t("aboutPlaceholder")}
                      label={t("introAbout")}
                      name="about"
                      rows={2}
                      errors={errors}
                      register={register}
                    />
                  </div>
                  {event?.isPromo && (
                    <div className="mb-4">
                      <Textarea
                        placeholder={t("subTitlePlaceholder")}
                        label={t("subTitle")}
                        name="subTitle"
                        rows={2}
                        errors={errors}
                        register={register}
                      />
                    </div>
                  )}
                  <div className="mb-12">
                    <div className="mb-4 flex w-full flex-col">
                      <label className="label">
                        <span className="label-text">
                          {t("eventType")}
                          <span className="text-warning">{` *`}</span>
                        </span>
                      </label>
                      <select
                        className="select-bordered select"
                        defaultValue={eventTypes[0].value}
                        required
                        {...register("type", { required: true })}
                      >
                        {eventTypes.map(({ value, label }) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                        {errors["type"] && (
                          <span className="pl-1 pt-1 text-xs text-warning">{t("requiredMessage")}</span>
                        )}
                      </select>
                    </div>
                  </div>
                  <h2 className="mt-8 mb-4">{t("guestGallery")}</h2>
                  <Toggle
                    label={t("showGuestGalleryTab")}
                    className="flex flex-col"
                    name="showGuestGalleryTab"
                    errors={errors}
                    getValues={getValues as any}
                    setValue={() => {}}
                    onClick={() => {
                      setValue("showGuestGalleryTab", !getValues("showGuestGalleryTab"), { shouldDirty: true });
                      forceUpdate();
                    }}
                  />
                  <h2 className="mt-8 mb-4">{t("betting")}</h2>
                  <Toggle
                    label={t("bettingEnabled")}
                    className="flex flex-col"
                    name="bettingEnabled"
                    errors={errors}
                    getValues={getValues as any}
                    setValue={() => {}}
                    onClick={() => {
                      const toggledOn = !getValues("bettingEnabled");
                      if (toggledOn) setShowBettingEnabledConfirm(true);
                      else setValue("bettingEnabled", false, { shouldDirty: true });
                      forceUpdate();
                    }}
                  />
                  <h2 className="mt-8 mb-4">{t("scoring")}</h2>
                  <Toggle
                    label={t("hideHighscores")}
                    className="flex flex-col"
                    name="hideHighscores"
                    errors={errors}
                    getValues={getValues as any}
                    setValue={() => {}}
                    onClick={() => {
                      setValue("hideHighscores", !getValues("hideHighscores"), { shouldDirty: true });
                      forceUpdate();
                    }}
                  />

                  <Toggle
                    label={t("degradePoints")}
                    className="flex flex-col"
                    name="pointDegrade"
                    errors={errors}
                    getValues={getValues as any}
                    setValue={() => {}}
                    onClick={() => {
                      const toggledOn = !getValues("pointDegrade");
                      if (toggledOn) setShowDegradeConfirm(true);
                      else setValue("pointDegrade", false, { shouldDirty: true });
                      forceUpdate();
                    }}
                  />
                  <ConfirmModal
                    show={showDegradeConfirm}
                    onCancel={() => {
                      setShowDegradeConfirm(false);
                    }}
                    onConfirm={() => {
                      setValue("pointDegrade", true, { shouldDirty: true });
                      setShowDegradeConfirm(false);
                    }}
                    type="success"
                    messageCopy={t("degradePointsConfirm")}
                    actionCopy={t("enable")}
                  />

                  {/* <h2 className="mt-8 mb-4">{t("oracleMode")}</h2>
                  <Toggle
                    label={t("oracleModeEnabled")}
                    className="flex flex-col"
                    name="oracleMode"
                    errors={errors}
                    getValues={getValues as any}
                    setValue={() => {}}
                    onClick={() => {
                      const toggledOn = !getValues("oracleMode");
                      if (toggledOn) setShowOracleModeConfirm(true);
                      else setValue("oracleMode", false, { shouldDirty: true });
                      forceUpdate();
                    }}
                  /> */}
                  <ConfirmModal
                    show={showOracleModeConfirm}
                    onCancel={() => {
                      setShowOracleModeConfirm(false);
                    }}
                    onConfirm={() => {
                      setValue("oracleMode", true, { shouldDirty: true });
                      setShowOracleModeConfirm(false);
                    }}
                    type="success"
                    messageCopy={t("oracleModeConfirm")}
                    actionCopy={t("enable")}
                  />

                  <h2 className="mt-8 mb-4">{t("teamManagement")}</h2>
                  <Toggle
                    label={t("playersOnly")}
                    className="flex flex-col"
                    name="playersOnly"
                    errors={errors}
                    getValues={getValues as any}
                    setValue={() => {}}
                    onClick={() => {
                      const toggledOn = !getValues("playersOnly");
                      if (toggledOn) setShowPlayersOnlyConfirm(true);
                      else setValue("playersOnly", false, { shouldDirty: true });
                      forceUpdate();
                    }}
                  />
                  <ConfirmModal
                    show={showPlayersOnlyConfirm}
                    onCancel={() => {
                      setShowPlayersOnlyConfirm(false);
                    }}
                    onConfirm={() => {
                      setValue("playersOnly", true, { shouldDirty: true });
                      setShowPlayersOnlyConfirm(false);
                    }}
                    type="success"
                    messageCopy={t("playersOnlyConfirm")}
                    actionCopy={t("enable")}
                  />
                  <Toggle
                    label={t("skipCreateTeam")}
                    className="flex flex-col"
                    name="skipCreateTeam"
                    errors={errors}
                    getValues={getValues as any}
                    setValue={() => {}}
                    onClick={() => {
                      const toggledOn = !getValues("skipCreateTeam");
                      if (toggledOn) setShowSkipCreateTeamConfirm(true);
                      else setValue("skipCreateTeam", false, { shouldDirty: true });
                      forceUpdate();
                    }}
                  />
                  <ConfirmModal
                    show={showSkipCreateTeamConfirm}
                    onCancel={() => {
                      setShowSkipCreateTeamConfirm(false);
                    }}
                    onConfirm={() => {
                      setValue("skipCreateTeam", true, { shouldDirty: true });
                      setShowSkipCreateTeamConfirm(false);
                    }}
                    type="success"
                    messageCopy={t("skipCreateTeamConfirm")}
                    actionCopy={t("enable")}
                  />

                  <Toggle
                    label={t("uploadersOnly")}
                    className="flex flex-col"
                    name="uploadersOnly"
                    errors={errors}
                    getValues={getValues as any}
                    setValue={() => {}}
                    onClick={() => {
                      const toggledOn = !getValues("uploadersOnly");
                      if (toggledOn) setShowUploadersOnlyConfirm(true);
                      else setValue("uploadersOnly", false, { shouldDirty: true });
                      forceUpdate();
                    }}
                  />
                  <ConfirmModal
                    show={showUploadersOnlyConfirm}
                    onCancel={() => {
                      setShowUploadersOnlyConfirm(false);
                    }}
                    onConfirm={() => {
                      setValue("uploadersOnly", true, { shouldDirty: true });
                      setShowUploadersOnlyConfirm(false);

                      // We also auto-hide highscores as a silent feature.
                      // We will not have challenges so it makes no sense to track points
                      setValue("hideHighscores", true, { shouldDirty: true });

                      // Disable betting if uploaders only is enabled because then there are no challenges
                      setValue("bettingEnabled", false, { shouldDirty: true });
                    }}
                    type="success"
                    messageCopy={t("uploadersOnlyConfirm")}
                    actionCopy={t("enable")}
                  />

                  <HowBettingWorksModal show={showBettingModal} onClose={() => setShowBettingModal(false)} />

                  <BettingConfigModal
                    show={showBettingEnabledConfirm}
                    onOpenBettingModal={() => setShowBettingModal(true)}
                    onCloseBettingModal={() => setShowBettingModal(false)}
                    onCancel={() => {
                      setShowBettingEnabledConfirm(false);
                    }}
                    onConfirm={(paymentUrl, shareWinningsWithHost) => {
                      setValue("bettingEnabled", true, { shouldDirty: true });
                      setValue("bettingPaymentUrl", paymentUrl, { shouldDirty: true });
                      setValue("shareWinningsWithHost", shareWinningsWithHost, { shouldDirty: true });
                      setShowBettingEnabledConfirm(false);
                    }}
                    initialPaymentUrl={getValues("bettingPaymentUrl")}
                    initialShareWinningsWithHost={getValues("shareWinningsWithHost")}
                  />

                  <h2 className="mt-8 mb-4">{t("supportedLanguages")}</h2>
                  <div className="form-control">
                    <label className="label cursor-pointer">
                      <span className="label-text">{t("english")}</span>
                      <input
                        type="checkbox"
                        checked={enLocale}
                        className={`checkbox ${enLocale ? "checkbox-primary" : ""}`}
                        onClick={() => handleLanguageToggle("en", enLocale)}
                      />
                    </label>
                  </div>
                  <div className="form-control">
                    <label className="label cursor-pointer">
                      <span className="label-text">{t("lithuanian")}</span>
                      <input
                        type="checkbox"
                        className={`checkbox ${ltLocale ? "checkbox-primary" : ""}`}
                        checked={ltLocale}
                        onClick={() => handleLanguageToggle("lt", ltLocale)}
                      />
                    </label>
                  </div>
                  <div className="form-control">
                    <label className="label cursor-pointer">
                      <span className="label-text">{t("swedish")}</span>
                      <input
                        type="checkbox"
                        checked={svLocale}
                        className={`checkbox ${svLocale ? "checkbox-primary" : ""}`}
                        onClick={() => handleLanguageToggle("sv", svLocale)}
                      />
                    </label>
                  </div>
                  <div className="form-control">
                    <label className="label cursor-pointer">
                      <span className="label-text">{t("german")}</span>
                      <input
                        type="checkbox"
                        checked={deLocale}
                        className={`checkbox ${deLocale ? "checkbox-primary" : ""}`}
                        onClick={() => handleLanguageToggle("de", deLocale)}
                      />
                    </label>
                  </div>
                  <div className="form-control">
                    <label className="label cursor-pointer">
                      <span className="label-text">{t("spanish")}</span>
                      <input
                        type="checkbox"
                        checked={esLocale}
                        className={`checkbox ${esLocale ? "checkbox-primary" : ""}`}
                        onClick={() => handleLanguageToggle("es", esLocale)}
                      />
                    </label>
                  </div>
                  <div className="form-control">
                    <label className="label cursor-pointer">
                      <span className="label-text">{t("italian")}</span>
                      <input
                        type="checkbox"
                        checked={itLocale}
                        className={`checkbox ${itLocale ? "checkbox-primary" : ""}`}
                        onClick={() => handleLanguageToggle("it", itLocale)}
                      />
                    </label>
                  </div>
                  <div className="form-control">
                    <label className="label cursor-pointer">
                      <span className="label-text">{t("french")}</span>
                      <input
                        type="checkbox"
                        checked={frLocale}
                        className={`checkbox ${frLocale ? "checkbox-primary" : ""}`}
                        onClick={() => handleLanguageToggle("fr", frLocale)}
                      />
                    </label>
                  </div>
                  <div className="form-control">
                    <label className="label cursor-pointer">
                      <span className="label-text">{t("polish")}</span>
                      <input
                        type="checkbox"
                        checked={plLocale}
                        className={`checkbox ${plLocale ? "checkbox-primary" : ""}`}
                        onClick={() => handleLanguageToggle("pl", plLocale)}
                      />
                    </label>
                  </div>
                  <div className="form-control">
                    <label className="label cursor-pointer">
                      <span className="label-text">{t("latvian")}</span>
                      <input
                        type="checkbox"
                        checked={lvLocale}
                        className={`checkbox ${lvLocale ? "checkbox-primary" : ""}`}
                        onClick={() => handleLanguageToggle("lv", lvLocale)}
                      />
                    </label>
                  </div>
                  <div className="form-control">
                    <label className="label cursor-pointer">
                      <span className="label-text">{t("portuguese")}</span>
                      <input
                        type="checkbox"
                        checked={ptLocale}
                        className={`checkbox ${ptLocale ? "checkbox-primary" : ""}`}
                        onClick={() => handleLanguageToggle("pt", ptLocale)}
                      />
                    </label>
                  </div>
                  <div className="form-control">
                    <label className="label cursor-pointer">
                      <span className="label-text">{t("chinese")}</span>
                      <input
                        type="checkbox"
                        checked={zhLocale}
                        className={`checkbox ${zhLocale ? "checkbox-primary" : ""}`}
                        onClick={() => handleLanguageToggle("zh", zhLocale)}
                      />
                    </label>
                  </div>
                </div>
                <div className={`w-full ${activeTab === 2 ? "" : "hidden"}`}>
                  <div className="text-center text-sm font-light">{t("backgroundPhotoAbout")}</div>
                  <FullPicker
                    defaultImageUri={`events/${eventId}/background_light`}
                    getFreshDefault={true}
                    uploadLabel={t("upload")}
                    reuploadLabel={t("reupload")}
                    onNewImage={setImage}
                    allowUpload
                    imageType="background"
                  />
                  <Toggle
                    label={t("enableLandingPage")}
                    name="landingConfig.enabled"
                    errors={errors}
                    getValues={getValues as any}
                    setValue={() => {}}
                    onClick={() => {
                      const enabled = !getValues("landingConfig.enabled");
                      const newValues = { ...getValues("landingConfig"), enabled };
                      setValue("landingConfig", newValues, { shouldDirty: true });
                      forceUpdate();
                    }}
                    tooltip={t("enableLandingPageTooltip")}
                  />
                  {getValues("landingConfig.enabled") && (
                    <div className="mt-4">
                      <h2 className="mt-8 mb-4">{t("landingPageConfig")}</h2>

                      <div className="mb-4">
                        <Textarea
                          placeholder={t("invitationCopyPlaceholder")}
                          label={t("invitationCopy")}
                          name="landingConfig.invitationCopy"
                          rows={3}
                          errors={errors}
                          register={register}
                        />
                      </div>

                      <div className="form-control">
                        <label className="label cursor-pointer">
                          <span className="label-text">{t("showDate")}</span>
                          <input
                            type="checkbox"
                            className={`toggle ${getValues("landingConfig.showDate") ? "toggle-primary" : ""}`}
                            checked={getValues("landingConfig.showDate")}
                            onChange={() => {
                              setValue("landingConfig.showDate", !getValues("landingConfig.showDate"), {
                                shouldDirty: true,
                              });
                              forceUpdate();
                            }}
                          />
                        </label>
                      </div>

                      <div className="form-control">
                        <label className="label cursor-pointer">
                          <span className="label-text">{t("showCountdown")}</span>
                          <input
                            type="checkbox"
                            className={`toggle ${getValues("landingConfig.showCountdown") ? "toggle-primary" : ""}`}
                            checked={getValues("landingConfig.showCountdown")}
                            onChange={() => {
                              setValue("landingConfig.showCountdown", !getValues("landingConfig.showCountdown"), {
                                shouldDirty: true,
                              });
                              forceUpdate();
                            }}
                          />
                        </label>
                      </div>

                      {/* <div className="form-control">
                      <label className="label cursor-pointer">
                        <span className="label-text">{t("showLocation")}</span>
                        <input
                          type="checkbox"
                          className={`toggle ${getValues("landingConfig.showLocation") ? "toggle-primary" : ""}`}
                          checked={getValues("landingConfig.showLocation")}
                          onChange={() => {
                            setValue("landingConfig.showLocation", !getValues("landingConfig.showLocation"));
                            forceUpdate();
                          }}
                        />
                      </label>
                    </div>

                    {getValues("landingConfig.showLocation") && (
                      <>
                        <div className="mb-4">
                          <Input
                            label={t("locationName")}
                            name="landingConfig.locationName"
                            placeholder={t("locationNameExample")}
                            errors={errors}
                            register={register}
                          />
                        </div>
                        <div className="mb-4">
                          <Input
                            label={t("locationCoords")}
                            name="landingConfig.locationCoords"
                            placeholder={t("locationCoordsExample")}
                            errors={errors}
                            register={register}
                          />
                          <p className="mt-1 text-xs text-gray-500">{t("locationCoordsHelp")}</p>
                        </div>
                      </>
                    )} */}
                    </div>
                  )}
                </div>
                <div className={`${activeTab === 3 ? "" : "hidden"}`}>
                  <div className="text-center text-sm font-light">{t("logoUploadAbout")}</div>
                  <FullPicker
                    defaultImageUri={`events/${eventId}/logo.png`}
                    getFreshDefault={true}
                    uploadLabel={t("upload")}
                    reuploadLabel={t("reupload")}
                    onNewImage={setLogo}
                    allowUpload
                    imageType="logo"
                  />
                  {event?.isPromo && (
                    <>
                      <h2 className="mt-8 mb-4 text-center">{t("defaultTeamAvatar")}</h2>
                      <div className="text-center text-sm font-light">{t("defaultTeamAvatarAbout")}</div>
                      <FullPicker
                        defaultImageUri={`events/${eventId}/defaultAvatar_heavy`}
                        getFreshDefault={true}
                        uploadLabel={t("upload")}
                        reuploadLabel={t("reupload")}
                        onNewImage={setDefaultAvatar}
                        allowUpload
                        imageType="defaultAvatar"
                      />
                    </>
                  )}
                </div>
                <div className="mb-12 mt-8 flex justify-center">
                  <button
                    type="submit"
                    disabled={saving}
                    className={`inline-flex w-full justify-center rounded-md bg-primary px-8 py-3 text-sm font-semibold text-white shadow-sm hover:bg-base-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2 ${
                      saving ? "loading" : ""
                    }`}
                    id="submit-button"
                  >
                    {t("save")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </main>
      </div>

      {/* Unsaved Changes Modal */}
      <Modal show={showUnsavedChangesModal} onCancel={() => setShowUnsavedChangesModal(false)} size="sm">
        <h3 className="text-lg font-bold">{t("unsavedChanges")}</h3>
        <p className="py-4">{t("unsavedChangesMessage")}</p>
        <div className="flex flex-col gap-3 px-4 py-3">
          <button
            type="button"
            className={`inline-flex w-full justify-center rounded-md bg-primary px-8 py-3 text-sm font-semibold text-white shadow-sm hover:bg-base-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2 ${
              saving ? "loading" : ""
            }`}
            onClick={handleSaveAndLeave}
          >
            {t("saveAndLeave")}
          </button>
          <button
            type="button"
            className="btn-error btn-link inline-flex justify-center rounded-md px-3 py-1 text-xs font-semibold text-red-600 sm:w-auto"
            onClick={handleLeaveWithoutSaving}
          >
            {t("leaveWithoutSaving")}
          </button>
          <button
            type="button"
            className="mt-4 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:w-auto"
            onClick={() => setShowUnsavedChangesModal(false)}
          >
            {t("cancel")}
          </button>
        </div>
      </Modal>
    </Loading>
  );
};
